(function () {
  const qtyMap = new Map(); // variantId -> committed quantity in cart
  const desiredQtyMap = new Map(); // variantId -> optimistic target quantity
  const syncInFlight = new Set(); // variantIds currently syncing with server
  const firstAddLockSet = new Set(); // variantIds locked for first 0->1 add only
  const syncStartQtyMap = new Map(); // variantId -> qty before sync cycle
  const interactionContextMap = new Map(); // variantId -> { searchContext, notificationContext }
  const variantTitleMap = new Map(); // variantId -> cached title
  const variantImageMap = new Map(); // variantId -> cached image

  let toastOffsetListenersBound = false;

  const themeRoutes = window?.Theme?.routes || {};
  const cartUrl =
    typeof themeRoutes.cart_url === 'string' && themeRoutes.cart_url.length > 0
      ? themeRoutes.cart_url
      : '/cart';
  const cartJsUrl = cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  const cartUpdateUrl =
    typeof themeRoutes.cart_update_url === 'string' && themeRoutes.cart_update_url.length > 0
      ? themeRoutes.cart_update_url
      : '/cart/update.js';
  const cartAddUrl =
    typeof themeRoutes.cart_add_url === 'string' && themeRoutes.cart_add_url.length > 0
      ? themeRoutes.cart_add_url
      : '/cart/add.js';
  const moneyFormat = window.tbMoneyFormat || '{{amount}} €';
  const qtyStrings = window.tbQtyStrings || {};

  const globalQuickQtyMaxRaw = Number(document.body?.dataset?.tbQuickQtyMax);
  const globalQuickQtyMax =
    Number.isFinite(globalQuickQtyMaxRaw) && globalQuickQtyMaxRaw > 0
      ? Math.trunc(globalQuickQtyMaxRaw)
      : null;

  function isValidVariantId(value) {
    const id = Number(value);
    return Number.isFinite(id) && id > 0;
  }

  function normalizeVariantId(value) {
    return Number(value);
  }

  function clampQty(rawQty) {
    let qty = Math.max(0, Number(rawQty) || 0);
    if (Number.isFinite(globalQuickQtyMax) && globalQuickQtyMax > 0) {
      qty = Math.min(globalQuickQtyMax, qty);
    }
    return qty;
  }

  function formatMoney(cents) {
    const amount = (Number(cents || 0) / 100).toFixed(2).replace('.', ',');
    return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  // Dárkové řádky (přidané gift-progress featurou, property `_gift_progress`) spravuje
  // výhradně gift-auto-add.js — nemají stepper a NESMÍ vyvolat vlastní toast ani se počítat
  // do sledovaného množství (jinak přidání dárku = druhý toast „vyskakující všelijak").
  function isGiftItem(item) {
    return Boolean(item && item.properties && item.properties._gift_progress);
  }

  function buildQtyMapFromCart(cart) {
    const map = new Map();
    for (const item of cart?.items || []) {
      if (isGiftItem(item)) continue;
      const id = Number(item?.variant_id);
      if (!isValidVariantId(id)) continue;
      map.set(id, (map.get(id) || 0) + (Number(item?.quantity) || 0));
    }
    return map;
  }

  function findPrimaryDelta(prevMap, nextMap) {
    const ids = new Set([...prevMap.keys(), ...nextMap.keys()]);
    let best = null;

    for (const id of ids) {
      const previousQty = prevMap.get(id) || 0;
      const nextQty = nextMap.get(id) || 0;
      const delta = nextQty - previousQty;
      if (delta === 0) continue;

      if (!best || Math.abs(delta) > Math.abs(best.delta)) {
        best = { variantId: id, previousQty, nextQty, delta };
      }
    }

    return best;
  }

  function getDisplayedQty(variantId) {
    const desiredQty = desiredQtyMap.get(variantId);
    if (Number.isFinite(desiredQty) && desiredQty >= 0) return desiredQty;
    return qtyMap.get(variantId) || 0;
  }

  function animateCtrlDelta(ctrl, previousQty, nextQty) {
    if (!ctrl || previousQty === nextQty) return;
    const bumpClass = nextQty > previousQty ? 'tb-qty-ctrl--bump-up' : 'tb-qty-ctrl--bump-down';
    ctrl.classList.remove('tb-qty-ctrl--bump-up', 'tb-qty-ctrl--bump-down');
    void ctrl.offsetWidth;
    ctrl.classList.add(bumpClass);
    window.setTimeout(() => {
      if (!ctrl.isConnected) return;
      ctrl.classList.remove('tb-qty-ctrl--bump-up', 'tb-qty-ctrl--bump-down');
    }, 360);
  }

  function renderCtrl(ctrl) {
    const variantId = normalizeVariantId(ctrl.dataset.variantId);
    if (!isValidVariantId(variantId)) return;

    const qty = getDisplayedQty(variantId);
    const previousQtyRaw = ctrl.dataset.renderedQty;
    const previousQty =
      previousQtyRaw !== undefined && previousQtyRaw !== '' ? Number(previousQtyRaw) || 0 : null;

    const numEl = ctrl.querySelector('.tb-qty-ctrl__num');
    const decBtn = ctrl.querySelector('.tb-qty-ctrl__dec');
    const incBtn = ctrl.querySelector('.tb-qty-ctrl__inc');
    const isCompact = ctrl.dataset.compact === 'true';
    const addLabelRaw = String(ctrl.dataset.addLabel || qtyStrings.addToCart || '').trim();
    const addLabel = addLabelRaw.length > 0 ? addLabelRaw : '';
    const reachedGlobalMax =
      Number.isFinite(globalQuickQtyMax) && globalQuickQtyMax > 0 ? qty >= globalQuickQtyMax : false;

    ctrl.classList.toggle('tb-qty-ctrl--empty', qty === 0);
    ctrl.dataset.qty = String(qty);

    if (numEl) {
      numEl.textContent = String(qty);
      numEl.hidden = qty === 0;
    }

    if (decBtn) {
      decBtn.hidden = qty === 0;
    }

    if (incBtn) {
      incBtn.disabled = reachedGlobalMax;
      if (qty === 0) {
        if (!isCompact && ctrl.dataset.multilineLabel === 'true') {
          const labelSpan = document.createElement('span');
          labelSpan.className = 'tb-qty-ctrl__label-text';
          labelSpan.textContent = addLabel;
          incBtn.replaceChildren(labelSpan);
        } else {
          incBtn.textContent = isCompact ? '+' : addLabel;
        }
        incBtn.classList.toggle('tb-qty-ctrl__inc--label', !isCompact);
      } else {
        incBtn.textContent = '+';
        incBtn.classList.remove('tb-qty-ctrl__inc--label');
      }
    }

    ctrl.dataset.renderedQty = String(qty);
    if (previousQty !== null) {
      animateCtrlDelta(ctrl, previousQty, qty);
    }
  }

  function renderVariant(variantId) {
    const controls = document.querySelectorAll(`.tb-qty-ctrl[data-variant-id="${variantId}"]`);
    for (const ctrl of controls) renderCtrl(ctrl);
  }

  function renderAll() {
    for (const ctrl of document.querySelectorAll('.tb-qty-ctrl[data-variant-id]')) {
      renderCtrl(ctrl);
    }
  }

  function setVariantClass(variantId, className, enabled) {
    const controls = document.querySelectorAll(`.tb-qty-ctrl[data-variant-id="${variantId}"]`);
    for (const ctrl of controls) {
      ctrl.classList.toggle(className, enabled);
    }
  }

  function setVariantLoading(variantId, isLoading) {
    setVariantClass(variantId, 'tb-qty-ctrl--loading', isLoading);
  }

  function setVariantFirstAddLock(variantId, isLocked) {
    const safeVariantId = normalizeVariantId(variantId);
    if (!isValidVariantId(safeVariantId)) return;

    if (isLocked) firstAddLockSet.add(safeVariantId);
    else firstAddLockSet.delete(safeVariantId);

    setVariantClass(safeVariantId, 'tb-qty-ctrl--first-add-lock', isLocked);
  }

  function setVariantAggregating(variantId, isAggregating) {
    const safeVariantId = normalizeVariantId(variantId);
    if (!isValidVariantId(safeVariantId)) return;
    setVariantClass(safeVariantId, 'tb-qty-ctrl--aggregating', isAggregating);
  }

  function findVariantTitle(ctrl) {
    if (!ctrl || !(ctrl instanceof Element)) return '';

    // Karta, ve které ovladač množství sedí. Když tu chybí kontejner nového místa,
    // spadne se na `ctrl.parentElement` — a ten bývá tak úzký, že se v něm název
    // produktu vůbec nenachází a vyhraje generický `a[href*="/products/"]`.
    // Přesně to dělal product-peek: root byl `.product-peek__actions`, kde je jediný
    // produktový odkaz „Zobrazit další podrobnosti", takže se ten text ukázal
    // jako název produktu v toastu (2026-09-12).
    const root =
      ctrl.closest(
        '.tpc-card, .tb-cart-rec-card, .tb-product-showcase__card, .predictive-search-results__card--product, .predictive-search-product-row-wrap, .tb-cart-add-notification__list-item, .cart-item, .cart-products__item, [data-product-peek-content], product-information, product-form-component'
      ) || ctrl.parentElement;
    if (!root) return '';

    // Pořadí je pravidlo, ne náhoda: konkrétní titulky nejdřív, generické nadpisy potom
    // a `a[href*="/products/"]` až úplně na konci. Ten odkaz je totiž poslední záchrana —
    // v kartě může vést na produkt i tlačítko „zobrazit detail", a když se dostane
    // před titulek, jeho text se ukáže místo názvu produktu.
    const selectors = [
      '.tpc-card__title',
      '.tb-cart-rec-card__title',
      '.tb-product-showcase__title',
      '.predictive-search-product-row__title',
      '.tb-cart-add-notification__list-title',
      '.cart-item__heading a',
      '.cart-items__title',
      '.cart-products__title a',
      '.product-peek__title',
      '.product__title',
      'h1',
      'h2',
      'h3',
      'a[href*="/products/"]',
    ];

    for (const selector of selectors) {
      const el = root.querySelector(selector);
      const raw = el?.textContent;
      if (!raw) continue;
      const normalized = raw.replace(/\s+/g, ' ').trim();
      if (normalized.length > 0) return normalized;
    }

    return '';
  }

  function findVariantImage(ctrl) {
    if (!ctrl || !(ctrl instanceof Element)) return '';

    const root =
      ctrl.closest(
        '.tpc-card, .tb-cart-rec-card, .tb-product-showcase__card, .predictive-search-results__card--product, .predictive-search-product-row-wrap, .tb-cart-add-notification__list-item, .cart-item, .cart-products__item, product-information, product-form-component'
      ) || ctrl.parentElement;
    if (!root) return '';

    const selectors = [
      '.tpc-card__img',
      '.tb-cart-rec-card__image',
      '.tb-product-showcase__media img',
      '.predictive-search-product-row__image',
      '.tb-cart-add-notification__list-img',
      '.cart-items__media-image',
      // PDP/card media galleries keep every variant's slide in the DOM at once (only one is the
      // active variant, via aria-hidden) — a bare 'img' below would grab whichever slide happens
      // to be first, not the one matching the variant that was actually just added.
      'slideshow-slide[aria-hidden="false"] img',
      'img',
    ];

    for (const selector of selectors) {
      const img = root.querySelector(selector);
      const src = img?.getAttribute('src') || img?.getAttribute('data-src');
      if (src && src.trim().length > 0) return src.trim();
    }

    return '';
  }

  function getVariantTitle(variantId, ctrl) {
    const safeVariantId = normalizeVariantId(variantId);
    if (!isValidVariantId(safeVariantId)) return qtyStrings.product || '';

    const fromDom = findVariantTitle(ctrl);
    if (fromDom) {
      variantTitleMap.set(safeVariantId, fromDom);
      return fromDom;
    }

    const remembered = variantTitleMap.get(safeVariantId);
    if (remembered && remembered.trim().length > 0) return remembered;

    const controlByVariant = document.querySelector(
      `.tb-qty-ctrl[data-variant-id="${safeVariantId}"], quantity-selector-component[data-variant-id="${safeVariantId}"], cart-quantity-selector-component[data-variant-id="${safeVariantId}"]`
    );
    const fallback = findVariantTitle(controlByVariant);
    if (fallback) {
      variantTitleMap.set(safeVariantId, fallback);
      return fallback;
    }

    return qtyStrings.product || '';
  }

  function getVariantImage(variantId, ctrl) {
    const safeVariantId = normalizeVariantId(variantId);
    if (!isValidVariantId(safeVariantId)) return '';

    const fromDom = findVariantImage(ctrl);
    if (fromDom) {
      variantImageMap.set(safeVariantId, fromDom);
      return fromDom;
    }

    const remembered = variantImageMap.get(safeVariantId);
    if (remembered) return remembered;

    const controlByVariant = document.querySelector(
      `.tb-qty-ctrl[data-variant-id="${safeVariantId}"], quantity-selector-component[data-variant-id="${safeVariantId}"], cart-quantity-selector-component[data-variant-id="${safeVariantId}"]`
    );
    const fallback = findVariantImage(controlByVariant);
    if (fallback) {
      variantImageMap.set(safeVariantId, fallback);
      return fallback;
    }

    return '';
  }

  function isSearchModalOpen() {
    return Boolean(
      document.querySelector('#search-modal dialog[open], .search-modal__content[open], .search-modal dialog[open]')
    );
  }

  function getOpenSearchDialog() {
    const dialog = document.querySelector(
      '#search-modal dialog[open], .search-modal__content[open], .search-modal dialog[open]'
    );
    return dialog instanceof HTMLElement ? dialog : null;
  }

  function isElementVisibleForOffset(el) {
    if (!(el instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    const rect = el.getBoundingClientRect();
    if (rect.height <= 0 || rect.width <= 0) return false;
    if (rect.bottom <= 0) return false;
    return true;
  }

  function getVisibleHeaderBottom() {
    const headerGroup = document.querySelector('#header-group');
    let maxBottom = 0;

    if (headerGroup instanceof HTMLElement) {
      for (const child of Array.from(headerGroup.children)) {
        if (!(child instanceof HTMLElement)) continue;
        if (!isElementVisibleForOffset(child)) continue;
        const rect = child.getBoundingClientRect();
        maxBottom = Math.max(maxBottom, rect.bottom);
      }
    }

    if (maxBottom > 0) return maxBottom;

    const topRow = document.querySelector('#header-component .header__row--top');
    if (isElementVisibleForOffset(topRow)) return topRow.getBoundingClientRect().bottom;

    const headerComponent = document.querySelector('#header-component');
    if (isElementVisibleForOffset(headerComponent)) return headerComponent.getBoundingClientRect().bottom;

    const headerSection = document.querySelector('.header-section');
    if (isElementVisibleForOffset(headerSection)) return headerSection.getBoundingClientRect().bottom;

    return 0;
  }

  function updateToastOffset(region) {
    if (!region) return;

    if (isSearchModalOpen()) {
      const searchDialog = getOpenSearchDialog();
      if (searchDialog && region.parentElement !== searchDialog) {
        searchDialog.appendChild(region);
      }
      region.classList.add('tb-qty-toast-region--search-open');
      region.style.top = '12px';
      region.style.right = '12px';
      return;
    }

    if (region.parentElement !== document.body) {
      document.body.appendChild(region);
    }

    region.classList.remove('tb-qty-toast-region--search-open');
    region.style.right = '';

    let topOffset = 10;
    const visibleBottom = getVisibleHeaderBottom();
    if (visibleBottom > 0) {
      topOffset = Math.max(topOffset, Math.round(visibleBottom) + 8);
    }

    region.style.top = `${topOffset}px`;
  }

  function ensureToastRegion() {
    let region = document.querySelector('.tb-qty-toast-region');
    if (region) return region;

    region = document.createElement('div');
    region.className = 'tb-qty-toast-region';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
    updateToastOffset(region);

    if (!toastOffsetListenersBound) {
      const update = () => {
        const existingRegion = document.querySelector('.tb-qty-toast-region');
        if (existingRegion) updateToastOffset(existingRegion);
      };

      window.addEventListener('resize', update, { passive: true });
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('focus', update, { passive: true });
      document.addEventListener('toggle', update, true);
      toastOffsetListenersBound = true;
    }

    return region;
  }

  function showMiniToast(type, title, currentQuantity, deltaQuantity, imageSrc) {
    const region = ensureToastRegion();
    updateToastOffset(region);

    const isDiscountToast = type === 'discount';
    const toast = document.createElement('div');
    toast.className = `tb-qty-toast${isDiscountToast ? ' tb-qty-toast--no-media' : ''}`;
    toast.setAttribute('role', 'status');
    toast.innerHTML = isDiscountToast
      ? [
          '<div class="tb-qty-toast__text-wrap">',
          '<p class="tb-qty-toast__title"></p>',
          '<p class="tb-qty-toast__meta"></p>',
          '</div>',
        ].join('')
      : [
          '<div class="tb-qty-toast__media" aria-hidden="true"></div>',
          '<div class="tb-qty-toast__text-wrap">',
          '<p class="tb-qty-toast__title"></p>',
          '<p class="tb-qty-toast__meta"></p>',
          '</div>',
        ].join('');
    region.appendChild(toast);

    const mediaEl = toast.querySelector('.tb-qty-toast__media');
    const titleEl = toast.querySelector('.tb-qty-toast__title');
    const metaEl = toast.querySelector('.tb-qty-toast__meta');

    if (mediaEl && !isDiscountToast) {
      mediaEl.textContent = '';
      const safeSrc = String(imageSrc || '').trim();
      if (safeSrc) {
        const img = document.createElement('img');
        img.src = safeSrc;
        img.alt = '';
        img.loading = 'lazy';
        mediaEl.appendChild(img);
      }
    }

    if (titleEl) {
      titleEl.textContent = title || qtyStrings.product || '';
    }

    if (metaEl) {
      if (type === 'removed') {
        metaEl.textContent = qtyStrings.removed || '';
      } else if (type === 'discount') {
        metaEl.textContent = qtyStrings.updated || '';
      } else {
        const safeCurrent = Number(currentQuantity);
        const currentLabel = Number.isFinite(safeCurrent) && safeCurrent >= 0 ? String(safeCurrent) : '0';
        const safeDelta = Number(deltaQuantity);
        const toneClass =
          safeDelta > 0
            ? 'tb-qty-toast__value--positive'
            : safeDelta < 0
              ? 'tb-qty-toast__value--negative'
              : '';
        metaEl.textContent = `${qtyStrings.updated || ''}: `;
        const valueEl = document.createElement('span');
        valueEl.className = `tb-qty-toast__value ${toneClass}`;
        valueEl.textContent = currentLabel;
        metaEl.appendChild(valueEl);
      }
    }

    toast.classList.remove('is-added', 'is-removed', 'is-update-positive', 'is-update-negative', 'is-visible');
    if (type === 'removed') {
      toast.classList.add('is-removed');
    } else if (type === 'discount') {
      toast.classList.add('is-added', 'is-update-positive');
    } else {
      const safeDelta = Number(deltaQuantity);
      toast.classList.add('is-added', safeDelta >= 0 ? 'is-update-positive' : 'is-update-negative');
    }

    void toast.offsetWidth;
    toast.classList.add('is-visible');

    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => {
        toast.remove();
      }, 260);
    }, 2300);
  }

  // --- Dárek zdarma: samostatný ÚZKÝ toast, vyskočí SOUČASNĚ s produktovým ---
  // Prahy bereme z #gift-progress-config (cents). Překročení detekujeme na straně theme z mezisoučtu
  // košíku (bez dárkových řádků), takže gift toast naskočí ve stejný okamžik jako produktový —
  // nečekáme na asynchronní přidání dárkové položky z gift-auto-add.js.
  let giftTiersCents = null;
  function getGiftTiers() {
    if (giftTiersCents !== null) return giftTiersCents;
    giftTiersCents = [];
    try {
      const el = document.getElementById('gift-progress-config');
      if (el) {
        const cfg = JSON.parse(el.textContent || '{}');
        giftTiersCents = (cfg.tiers || [])
          .map((t) => Number(t.cents) || 0)
          .filter((c) => c > 0)
          .sort((a, b) => a - b);
      }
    } catch (e) {
      giftTiersCents = [];
    }
    return giftTiersCents;
  }

  // Task 9 (2026-09-10, otáčí Task 5c): dárkové produkty (Oat Bar, láhev, Hydration…) se
  // POČÍTAJÍ do prahu jako každé jiné zboží — jen poukazy + dosluhující mystery dárek
  // (typy `Dárkový poukaz`/`Dárek`) se nepočítají, stejně jako gift-auto-add.js a
  // gift-progress-bar.js. Zdroj: `#gift-progress-excluded-types` (renderuje se VŽDY,
  // nezávisle na gift_progress_enabled/gift_auto_add_enabled — statický seznam dvou typů).
  let excludedTypes = null;
  function getExcludedTypes() {
    if (excludedTypes !== null) return excludedTypes;
    excludedTypes = new Set(['Dárkový poukaz', 'Dárek']); // hardcoded fallback
    try {
      const el = document.getElementById('gift-progress-excluded-types');
      if (el) {
        const parsed = JSON.parse(el.textContent);
        if (Array.isArray(parsed)) {
          excludedTypes = new Set(parsed.map((t) => String(t)));
        }
      }
    } catch (e) {
      // Rozbitý JSON → zůstává hardcoded fallback výš.
    }
    return excludedTypes;
  }

  function nonGiftSubtotalCents(cart) {
    const types = getExcludedTypes();
    let sum = 0;
    for (const item of cart?.items || []) {
      if (isGiftItem(item)) continue;
      if (item?.gift_card) continue;
      if (types.has(item?.product_type)) continue;
      sum += Number(item?.final_line_price) || 0;
    }
    // Cart-level slevy (`allocation_method: across`) Shopify na řádky nealokuje, takže
    // `final_line_price` je nevidí. Bez téhle korekce by gift toast křičel „máš dárek"
    // u košíku, který po slevě práh nesplňuje. Plné odůvodnění: gift-progress-bar.js
    // #applyCartLevelDiscount — všechny čtyři výpočty eligible musí dát stejné číslo.
    const subtotal = Number(cart?.items_subtotal_price) || 0;
    const total = Number(cart?.total_price) || 0;
    if (subtotal <= 0 || total >= subtotal) return sum;
    return Math.floor((sum * total) / subtotal);
  }

  function qualifiedTierCount(cart) {
    const tiers = getGiftTiers();
    if (!tiers.length) return 0;
    const subtotal = nonGiftSubtotalCents(cart);
    let n = 0;
    for (const c of tiers) if (subtotal >= c) n += 1;
    return n;
  }

  let giftBaselineDone = false;
  let lastQualifiedTiers = 0;

  // Volá se z syncFromCart (běží ve všech cestách změny košíku). První (baseline) běh na loadu
  // NEtoastuje. Když počet splněných prahů vzroste (nový dárek odemčen) → samostatný gift toast.
  function maybeShowGiftToast(cart) {
    const n = qualifiedTierCount(cart);
    if (!giftBaselineDone) {
      giftBaselineDone = true;
      lastQualifiedTiers = n;
      return;
    }
    if (n > lastQualifiedTiers) showGiftToast();
    lastQualifiedTiers = n;
  }

  function showGiftToast() {
    const label = qtyStrings.giftUnlocked || '';
    if (!label) return;
    const region = ensureToastRegion();
    const toast = document.createElement('div');
    toast.className = 'tb-qty-toast tb-qty-toast--no-media tb-qty-toast--gift';
    toast.setAttribute('role', 'status');
    toast.innerHTML = '<div class="tb-qty-toast__text-wrap"><p class="tb-qty-toast__title"></p></div>';
    toast.querySelector('.tb-qty-toast__title').textContent = label;
    region.appendChild(toast);
    toast.classList.add('is-added', 'is-update-positive');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 260);
    }, 2300);
  }

  function isSearchContext(ctrl) {
    if (!ctrl || !(ctrl instanceof Element)) return false;
    return Boolean(ctrl.closest('.search-modal, .predictive-search, predictive-search-component'));
  }

  function isNotificationContext(ctrl) {
    if (!ctrl || !(ctrl instanceof Element)) return false;
    return Boolean(ctrl.closest('cart-add-notification, .tb-cart-add-notification'));
  }

  function isCartPage() {
    const bodyClassName = document.body?.className || '';
    if (/\btemplate-cart\b/.test(bodyClassName)) return true;

    const normalizedPathname = window.location.pathname.replace(/\/+$/, '');
    const normalizedCartPath = new URL(cartUrl, window.location.origin).pathname.replace(/\/+$/, '');
    if (normalizedPathname === normalizedCartPath) return true;

    return /\/cart$/i.test(normalizedPathname);
  }

  function isCartContext(ctrl) {
    if (isCartPage()) return true;
    if (!ctrl || !(ctrl instanceof Element)) return false;
    return Boolean(
      ctrl.closest(
        '.main-cart, .main-cart__content, cart-items-component, .cart-items, .cart-products, .cart-page, .cart__items'
      )
    );
  }

  // Gift lines must not count toward the cart bubble total (viz isGiftItem výše).
  function nonGiftItemCount(cart) {
    let count = 0;
    for (const item of cart?.items || []) {
      if (isGiftItem(item)) continue;
      count += Number(item?.quantity) || 0;
    }
    return count;
  }

  function dispatch(cart, extraData) {
    const data = Object.assign(
      { didError: false, itemCount: nonGiftItemCount(cart), source: 'tb-cart-qty' },
      extraData || {}
    );

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'tb-cart-qty', data },
      })
    );
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  async function fetchCart() {
    const response = await fetch(cartJsUrl, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  function syncFromCart(cart) {
    qtyMap.clear();
    for (const item of cart?.items || []) {
      if (isGiftItem(item)) continue; // dárek nemá stepper — mimo sledované množství i toasty
      const variantId = Number(item?.variant_id);
      if (!isValidVariantId(variantId)) continue;
      qtyMap.set(variantId, (qtyMap.get(variantId) || 0) + (Number(item?.quantity) || 0));

      // Zakešovať titul a obrázok z cart JSON — aby toast fungoval aj pri odstraňovaní
      // (keď je DOM položky košíka už preč pred zobrazením toastu).
      // Príchuť/balenie je samostatná varianta v košíku, preto title MUSÍ obsahovať aj
      // variant_title (napr. "420 g / Pomeranč") — čistý product_title by ich splietol dohromady.
      const baseTitle = String(item?.product_title || '').trim();
      const variantTitlePart = String(item?.variant_title || '').trim();
      const hasRealVariantTitle = variantTitlePart && variantTitlePart.toLowerCase() !== 'default title';
      const title = hasRealVariantTitle
        ? `${baseTitle} – ${variantTitlePart}`
        : baseTitle || String(item?.title || '').trim();
      if (title && !variantTitleMap.has(variantId)) {
        variantTitleMap.set(variantId, title);
      }
      const rawImage = item?.image || item?.featured_image?.url || '';
      if (rawImage && !variantImageMap.has(variantId)) {
        const imgUrl = String(rawImage).trim();
        variantImageMap.set(variantId, imgUrl.startsWith('//') ? `https:${imgUrl}` : imgUrl);
      }
    }

    for (const [variantId, desiredQty] of desiredQtyMap.entries()) {
      const committedQty = qtyMap.get(variantId) || 0;
      if (!syncInFlight.has(variantId) && committedQty === desiredQty) {
        desiredQtyMap.delete(variantId);
        interactionContextMap.delete(variantId);
      }
    }

    for (const variantId of firstAddLockSet) {
      if (!syncInFlight.has(variantId)) {
        setVariantFirstAddLock(variantId, false);
      }
    }

    maybeShowGiftToast(cart);
    renderAll();
  }

  function queueQuantityChange(variantId, nextQty, ctrl) {
    const safeVariantId = normalizeVariantId(variantId);
    if (!isValidVariantId(safeVariantId)) return;

    const safeQty = clampQty(nextQty);
    const displayedQty = getDisplayedQty(safeVariantId);
    if (safeQty === displayedQty) return;

    getVariantTitle(safeVariantId, ctrl);
    getVariantImage(safeVariantId, ctrl);

    desiredQtyMap.set(safeVariantId, safeQty);
    interactionContextMap.set(safeVariantId, {
      searchContext: isSearchContext(ctrl),
      notificationContext: isNotificationContext(ctrl),
      cartContext: isCartContext(ctrl),
    });

    if (syncInFlight.has(safeVariantId) && !firstAddLockSet.has(safeVariantId)) {
      setVariantAggregating(safeVariantId, true);
    }

    renderVariant(safeVariantId);
    void syncVariantQuantity(safeVariantId);
  }

  /**
   * Zapíše cílové množství ZÁKAZNÍKOVÝCH kusů varianty a vrátí celý košík.
   *
   * `/cart/update.js` klíčovaný VARIANT ID je nepoužitelný, jakmile je varianta v košíku
   * na víc řádcích — a to jsou dva běžné stavy, ne exotika (změřeno proti theme dev):
   *   - dárek zdarma (`_gift_progress`) + zákazníkův placený kus téže tyčinky:
   *     `{varianta: 1}` je no-op, protože dárkový řádek už 1 ks má → „nejde přidat do košíku";
   *   - řádek rozdělený BXGY slevou s prahem útraty (20 ks = 1 + 19): `{varianta: 21}`
   *     zapíše 21 do OBOU řádků → z 20 ks je rázem 40.
   * Proto: přírůstek přes `/cart/add.js` (bez properties → vznikne, nebo naroste, zákazníkův
   * vlastní řádek vedle dárkového), úbytek přes POZIČNÍ pole množství pro celý košík.
   * Dárkový řádek tím neadresujeme nikdy — ten patří výhradně gift-auto-add.js.
   */
  async function applyVariantQuantity(variantId, targetQty, committedQty) {
    if (targetQty > committedQty) {
      await postJson(cartAddUrl, { id: variantId, quantity: targetQty - committedQty });
      // `cart/add.js` vrací JEN přidané řádky, ne celý košík (jiný tvar odpovědi než
      // update.js) — pro dispatch i pro qtyMap potřebujeme plný košík.
      return fetchCart();
    }

    // Úbytek: čerstvý košík + poziční pole množství, stejný postup a ze stejného důvodu jako
    // `applyGroupQuantity` v assets/cart-merged-lines.js — klíč řádku Shopify přepisuje při
    // každé realokaci slev (a ta se u dárkové BXGY mění s každou změnou košíku), kdežto pole
    // je poziční a čte se těsně před zápisem, takže mezi čtením a zápisem není okno.
    // Rozdělené díly jedné varianty se přitom slijí do prvního — přesně tak je i vykresluje
    // `cart-products.liquid`. Řádky ostatních variant i dárkové řádky si drží své množství.
    const cart = await fetchCart();
    const updates = (cart.items || []).map((item) => Number(item?.quantity) || 0);

    let matched = 0;
    (cart.items || []).forEach((item, index) => {
      if (isGiftItem(item)) return;
      if (Number(item?.variant_id) !== variantId) return;
      updates[index] = matched === 0 ? targetQty : 0;
      matched += 1;
    });

    // Řádek mezitím zmizel (jiná záložka, checkout) — radši nezapisovat nic než naslepo.
    if (matched === 0) return cart;
    return postJson(cartUpdateUrl, { updates: updates });
  }

  async function syncVariantQuantity(variantId) {
    const safeVariantId = normalizeVariantId(variantId);
    if (!isValidVariantId(safeVariantId)) return;
    if (syncInFlight.has(safeVariantId)) return;

    syncInFlight.add(safeVariantId);
    if (!syncStartQtyMap.has(safeVariantId)) {
      syncStartQtyMap.set(safeVariantId, qtyMap.get(safeVariantId) || 0);
    }
    setVariantLoading(safeVariantId, true);

    try {
      while (true) {
        const desiredQty = desiredQtyMap.get(safeVariantId);
        const committedQty = qtyMap.get(safeVariantId) || 0;

        if (!Number.isFinite(desiredQty) || desiredQty === committedQty) {
          setVariantAggregating(safeVariantId, false);
          break;
        }

        const targetQty = clampQty(desiredQty);
        const cart = await applyVariantQuantity(safeVariantId, targetQty, committedQty);

        const context = interactionContextMap.get(safeVariantId) || {};
        const didIncrease = targetQty > committedQty;
        const didDecrease = targetQty < committedQty;
        const searchContext = Boolean(context.searchContext);
        const notificationContext = Boolean(context.notificationContext);
        const cartContext = Boolean(context.cartContext);

        dispatch(cart, {
          action: didIncrease ? 'add' : didDecrease ? 'decrement' : 'set',
          variantId: safeVariantId,
          openNotification:
            didIncrease && committedQty === 0 && !searchContext && !notificationContext && !cartContext,
          context: searchContext ? 'search-modal' : notificationContext ? 'cart-notification' : 'default',
        });

        // Pojistka proti nekonečné smyčce requestů: `dispatch` výš projde přes vlastní
        // `cart:update` listener → `syncFromCart`, takže qtyMap už drží stav serveru. Když
        // se množství NEPOHNULO (server požadavek tiše ignoroval — donedávna přesně případ
        // dárkové varianty), další kolo by poslalo tentýž request znovu, a tak pořád dokola.
        // Zahodíme optimistický cíl a necháme stepper spadnout zpátky na realitu košíku.
        if ((qtyMap.get(safeVariantId) || 0) === committedQty) {
          desiredQtyMap.delete(safeVariantId);
          interactionContextMap.delete(safeVariantId);
          break;
        }
      }
    } catch (error) {
      console.debug('[tb-cart-qty] quantity sync failed:', error);
      try {
        syncFromCart(await fetchCart());
      } catch (_) {}
    } finally {
      syncInFlight.delete(safeVariantId);
      setVariantFirstAddLock(safeVariantId, false);

      const desiredQty = desiredQtyMap.get(safeVariantId);
      const committedQty = qtyMap.get(safeVariantId) || 0;
      if (!Number.isFinite(desiredQty) || desiredQty === committedQty) {
        desiredQtyMap.delete(safeVariantId);
        interactionContextMap.delete(safeVariantId);
      }

      setVariantLoading(safeVariantId, false);
      renderVariant(safeVariantId);

      const hasPendingDelta =
        desiredQtyMap.has(safeVariantId) && desiredQtyMap.get(safeVariantId) !== (qtyMap.get(safeVariantId) || 0);

      if (!hasPendingDelta) {
        setVariantAggregating(safeVariantId, false);

        const syncStartQty = syncStartQtyMap.get(safeVariantId);
        const finalQty = qtyMap.get(safeVariantId) || 0;
        const startQty = Number.isFinite(syncStartQty) ? syncStartQty : finalQty;
        const delta = finalQty - startQty;

        if (startQty > 0 && finalQty === 0) {
          showMiniToast('removed', getVariantTitle(safeVariantId), 0, 0, getVariantImage(safeVariantId));
        } else if (delta !== 0) {
          showMiniToast('updated', getVariantTitle(safeVariantId), finalQty, delta, getVariantImage(safeVariantId));
        }

        syncStartQtyMap.delete(safeVariantId);
      } else {
        setVariantAggregating(safeVariantId, true);
        void syncVariantQuantity(safeVariantId);
      }
    }
  }

  document.addEventListener('click', function (event) {
    const incBtn = event.target.closest('.tb-qty-ctrl__inc');
    if (incBtn) {
      event.preventDefault();
      event.stopPropagation();
      const ctrl = incBtn.closest('.tb-qty-ctrl[data-variant-id]');
      if (!ctrl) return;

      const variantId = normalizeVariantId(ctrl.dataset.variantId);
      if (!isValidVariantId(variantId)) return;
      if (firstAddLockSet.has(variantId)) return;

      const currentQty = getDisplayedQty(variantId);
      const committedQty = qtyMap.get(variantId) || 0;
      if (currentQty === 0 && committedQty === 0 && !syncInFlight.has(variantId)) {
        setVariantFirstAddLock(variantId, true);
      }

      queueQuantityChange(variantId, currentQty + 1, ctrl);
      return;
    }

    const decBtn = event.target.closest('.tb-qty-ctrl__dec');
    if (decBtn) {
      event.preventDefault();
      event.stopPropagation();
      const ctrl = decBtn.closest('.tb-qty-ctrl[data-variant-id]');
      if (!ctrl) return;

      const variantId = normalizeVariantId(ctrl.dataset.variantId);
      if (!isValidVariantId(variantId)) return;
      if (firstAddLockSet.has(variantId)) return;

      const currentQty = getDisplayedQty(variantId);
      if (currentQty > 0) {
        queueQuantityChange(variantId, currentQty - 1, ctrl);
      }
    }
  }, true);

  document.addEventListener('cart:update', function (event) {
    const cart = event?.detail?.resource;
    if (!(cart && Array.isArray(cart.items))) return;

    if (event?.detail && event.detail.sourceId !== 'tb-cart-qty') {
      const previousMap = new Map(qtyMap);
      const nextMap = buildQtyMapFromCart(cart);
      const delta = findPrimaryDelta(previousMap, nextMap);

      if (delta) {
        const title = getVariantTitle(delta.variantId);
        const image = getVariantImage(delta.variantId);
        if (delta.previousQty > 0 && delta.nextQty === 0) {
          showMiniToast('removed', title, 0, 0, image);
        } else if (delta.delta !== 0) {
          showMiniToast('updated', title, delta.nextQty, delta.delta, image);
        }
      }
    }

    syncFromCart(cart);
  });

  document.addEventListener('tb:qty-discount-applied', function (event) {
    const discountType = event?.detail?.discountType === 'percentage' ? 'percentage' : 'fixed';
    const discountValue = Number(event?.detail?.discountValue);
    const discountCents = Number(event?.detail?.discountCents);
    if (discountType === 'percentage') {
      if (!Number.isFinite(discountValue) || discountValue <= 0) return;
    } else if (!Number.isFinite(discountCents) || discountCents <= 0) {
      return;
    }
    window.setTimeout(() => {
      showMiniToast('discount', qtyStrings.updated || '', 0, { discountType, discountValue, discountCents }, '');
    }, 220);
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const el = /** @type {Element} */ (node);
        if (el.matches && el.matches('.tb-qty-ctrl[data-variant-id]')) {
          renderCtrl(/** @type {HTMLElement} */ (el));
        }

        const descendants = el.querySelectorAll ? el.querySelectorAll('.tb-qty-ctrl[data-variant-id]') : [];
        for (const descendant of descendants) {
          renderCtrl(/** @type {HTMLElement} */ (descendant));
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Cart drawer: obsah (vč. quick-add stepperů doporučených produktů) se při otevření
  // A při každé změně košíku renderuje/morphuje IN-PLACE přes Section Rendering, což hlavní
  // MutationObserver výše (sleduje jen addedNodes) nezachytí → stepper doporučeného produktu,
  // který UŽ je v košíku, se resetoval na server-renderovanou 0 místo skutečného počtu ks.
  // Proto sledujeme drawer zvlášť a po každém morphu překreslíme steppery z qtyMap. Aby
  // renderAll (který sám mění DOM stepperů) nespustil nekonečnou smyčku, observer na dobu
  // renderAll odpojíme a hned zase připojíme (jeho vlastní mutace se tím nezaznamenají).
  function bindCartDrawerRerender() {
    const dialog = document.querySelector('.cart-drawer__dialog');
    if (!dialog || dialog.dataset.tbQtyRerenderBound === 'true') return;
    dialog.dataset.tbQtyRerenderBound = 'true';

    let scheduled = false;
    const contentObserver = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        contentObserver.disconnect();
        renderAll();
        contentObserver.observe(dialog, { childList: true, subtree: true });
      });
    });
    contentObserver.observe(dialog, { childList: true, subtree: true });
  }
  bindCartDrawerRerender();

  function updatePdpVariant(event) {
    const variant = event?.detail?.resource;
    if (!variant || !variant.id) return;
    const productId = event?.detail?.data?.productId;
    for (const ctrl of document.querySelectorAll('.tb-pdp-qty[data-variant-id]')) {
      if (productId && ctrl.dataset.productId && String(ctrl.dataset.productId) !== String(productId)) continue;
      ctrl.dataset.variantId = String(variant.id);
      renderCtrl(/** @type {HTMLElement} */ (ctrl));
    }
  }

  document.addEventListener('variant:selected', updatePdpVariant);
  document.addEventListener('variant:update', updatePdpVariant);

  async function refresh() {
    try {
      syncFromCart(await fetchCart());
    } catch (_) {}
  }

  document.addEventListener('cart:add', refresh);

  window.TBCartQty = { renderAll, refresh, syncFromCart };
  refresh();
})();
